/**
 * Firebase Configuration
 * 
 * Initializes Firebase Auth and Firestore.
 * Lazy-loads Firebase only when needed to keep initial bundle small.
 */

export const hasFirebaseConfig = !!import.meta.env.VITE_FIREBASE_API_KEY;

let _cached = null;

export async function getFirebase() {
  if (_cached) return _cached;
  if (!hasFirebaseConfig) return null;

  try {
    const { initializeApp } = await import('firebase/app');
    const fbAuth = await import('firebase/auth');
    const fbFirestore = await import('firebase/firestore');

    const app = initializeApp({
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
      appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
    });

    const auth = fbAuth.getAuth(app);
    const db = fbFirestore.getFirestore(app);
    const googleProvider = new fbAuth.GoogleAuthProvider();
    const appleProvider = new fbAuth.OAuthProvider('apple.com');
    appleProvider.addScope('email');
    appleProvider.addScope('name');

    _cached = {
      auth,
      db,
      googleProvider,
      appleProvider,
      // Auth methods
      signInWithPopup: fbAuth.signInWithPopup,
      signInWithPhoneNumber: fbAuth.signInWithPhoneNumber,
      RecaptchaVerifier: fbAuth.RecaptchaVerifier,
      sendSignInLinkToEmail: fbAuth.sendSignInLinkToEmail,
      isSignInWithEmailLink: fbAuth.isSignInWithEmailLink,
      signInWithEmailLink: fbAuth.signInWithEmailLink,
      signOut: fbAuth.signOut,
      onAuthStateChanged: fbAuth.onAuthStateChanged,
      // Firestore methods
      doc: fbFirestore.doc,
      getDoc: fbFirestore.getDoc,
      setDoc: fbFirestore.setDoc,
    };
    return _cached;
  } catch (e) {
    console.warn('Firebase init failed:', e.message);
    return null;
  }
}
